/**
 * Top-level URL segments reserved for app/marketing routes (not creator public pages).
 * Used by routing so /login, /subscribe, etc. are never treated as creator usernames.
 */
export const RESERVED_APP_TOP_LEVEL_SEGMENTS = new Set([
  "",
  "login",
  "login-live",
  "login-admin",
  "onboarding",
  "website-onboarding",
  "website-onboarding-major",
  "website-onboarding-full",
  "claim",
  "googleCallback",
  "auth",
  "subscribe",
  "subscribe-website",
  "pricing",
  "payment-canceled",
  "payment-success",
  "payment-cancel",
  "add-team-payment-success",
  "order",
  "checkout",
  "product-checkout",
  "cart",
  "stripe",
  "onboarding-success",
  "loading",
  "dashboard",
  "preview",
  "embed",
  "embeddemo",
  "website",
  "global",
  "private-beta",
  "no-user-found",
  "404",
  "401",
  "masterclass",
  "caroline-and-john",
  "privacy",
  "terms",
  "accept-invite",
  "download",
  "download-admin",
  "waitlist",
  "api",
]);

export function getTopLevelPathSegment(pathname: string): string {
  return pathname.split("/").filter(Boolean)[0] ?? "";
}

export function isReservedAppPath(pathname?: string): boolean {
  const path =
    pathname ??
    (typeof window !== "undefined" ? window.location.pathname : "/");
  const segment = getTopLevelPathSegment(path);
  // Root `/` is the creator home on subdomains/custom domains — must be spoofed to `/:slug`.
  if (!segment) return false;
  return RESERVED_APP_TOP_LEVEL_SEGMENTS.has(segment);
}

/** Creator public routes on a mapped host (`user.browsable.bio/embed`, etc.). */
export function isCreatorHostPublicPath(pathname?: string): boolean {
  const path =
    pathname ??
    (typeof window !== "undefined" ? window.location.pathname : "/");
  const normalized = path.replace(/\/+$/, "") || "/";
  return (
    normalized === "/embed" ||
    normalized === "/preview" ||
    normalized.startsWith("/embed/") ||
    normalized.startsWith("/preview/")
  );
}

/**
 * Whether a path on a creator subdomain/custom domain should be rewritten internally.
 * `/embed` and `/preview` are reserved on apex hosts but are valid creator URLs on mapped hosts.
 */
export function shouldSpoofCreatorHostPath(pathname?: string): boolean {
  if (isCreatorHostPublicPath(pathname)) return true;
  return !isReservedAppPath(pathname);
}
